import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import * as React from 'react';
import { isBoolean } from '../utils/lang.js';
import { noop } from '../utils/util.js';

var DEFAULT_SHOW_DELAY = 30;
var DEFAULT_HIDE_DELAY = 300;
function useTooltipHover(_ref) {
  var _ref$hideDelay = _ref.hideDelay,
    hideDelay = _ref$hideDelay === void 0 ? DEFAULT_HIDE_DELAY : _ref$hideDelay,
    _ref$interactive = _ref.interactive,
    interactive = _ref$interactive === void 0 ? false : _ref$interactive,
    onHide = _ref.onHide,
    onShow = _ref.onShow,
    open = _ref.open,
    _ref$showDelay = _ref.showDelay,
    showDelay = _ref$showDelay === void 0 ? DEFAULT_SHOW_DELAY : _ref$showDelay;
  var _React$useState = React.useState(!!open),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    openState = _React$useState2[0],
    setOpenState = _React$useState2[1];
  var openRef = React.useRef(open);
  var closeTimeoutRef = React.useRef(null);
  var moveTimeoutRef = React.useRef(null);
  var clearCloseTimeout = React.useCallback(function () {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);
  var clearMoveTimeout = React.useCallback(function () {
    if (moveTimeoutRef.current) {
      clearTimeout(moveTimeoutRef.current);
      moveTimeoutRef.current = null;
    }
  }, []);
  var openTooltip = React.useCallback(function (e) {
    clearCloseTimeout();
    setOpenState(true);
    if (onShow) onShow(e);
  }, [clearCloseTimeout, onShow]);
  var closeTooltip = React.useCallback(function (e) {
    clearCloseTimeout();
    setOpenState(false);
    if (onHide) onHide(e);
  }, [clearCloseTimeout, onHide]);
  var closeAfterDelay = React.useCallback(function (e) {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(function () {
      return closeTooltip(e);
    }, hideDelay);
  }, [clearCloseTimeout, hideDelay, closeTooltip]);
  var onTriggerMouseMove = React.useCallback(function (e) {
    clearMoveTimeout();
    moveTimeoutRef.current = setTimeout(function () {
      clearMoveTimeout();
      openTooltip(e);
    }, showDelay);
  }, [clearMoveTimeout, openTooltip, showDelay]);
  var onTriggerMouseLeave = React.useCallback(function (e) {
    clearMoveTimeout();
    if (interactive) {
      closeAfterDelay(e);
    } else {
      closeTooltip(e);
    }
  }, [clearMoveTimeout, interactive, closeTooltip, closeAfterDelay]);
  React.useEffect(function () {
    if (typeof openRef.current === "boolean" && open === void 0) {
      closeTooltip();
    }
    openRef.current = open;
  }, [open, openState, closeTooltip, openTooltip]);
  React.useEffect(function () {
    return function () {
      clearCloseTimeout();
      clearMoveTimeout();
    };
  }, [clearCloseTimeout, clearMoveTimeout]);
  return {
    open: isBoolean(open) ? open : openState,
    bindTrigger: {
      onFocus: onTriggerMouseMove,
      onBlur: open ? noop : onTriggerMouseLeave,
      onMouseMove: onTriggerMouseMove,
      onMouseLeave: open ? noop : onTriggerMouseLeave
    },
    bindTooltip: {
      onMouseEnter: interactive ? clearCloseTimeout : noop,
      onMouseLeave: open || !interactive ? noop : closeAfterDelay
    }
  };
}

export { useTooltipHover };
